import React, { useId, useState } from 'react';
import { BarChart3, CheckCircle2, ChevronDown, Download, FileText, Lightbulb, MessageSquare, Sparkles, TrendingUp } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { openPhoebeChat } from './GlobalPhoebeChat';
type EvidenceItem = {
  label: string;
  value: string;
  detail: string;
  icon: React.ReactNode;
  accent: string;
  surface: string;
};
const EVIDENCE: EvidenceItem[] = [{
  label: 'Learning adoption',
  value: '80%',
  detail: 'of learners were active this month, up 20% from the prior period.',
  icon: <TrendingUp size={18} aria-hidden="true" data-id="element-1214" data-mp="1vztzbup2yj8k" />,
  accent: 'text-[#007558]',
  surface: 'bg-[#007558]/10'
}, {
  label: 'Learner sentiment',
  value: 'Predominantly 4–5/5',
  detail: 'Survey feedback describes the curriculum as practical, relevant, and easy to navigate.',
  icon: <CheckCircle2 size={18} aria-hidden="true" data-id="element-1215" data-mp="6l5ovz01vbd6" />,
  accent: 'text-[#30718D]',
  surface: 'bg-[#30718D]/10'
}, {
  label: 'Transfer to work',
  value: 'Immediate application',
  detail: 'Learners value leadership strategies and realistic workplace scenarios they can use right away.',
  icon: <Lightbulb size={18} aria-hidden="true" data-id="element-1216" data-mp="mrz79rhfy7el" />,
  accent: 'text-[#B77808]',
  surface: 'bg-[#F59E0B]/10'
}, {
  label: 'Strategic relevance',
  value: 'AI + healthcare',
  detail: 'AI-focused learning was timely and valuable for healthcare professionals.',
  icon: <BarChart3 size={18} aria-hidden="true" data-id="element-1217" data-mp="1ai385r38vt1w" />,
  accent: 'text-[#DB3725]',
  surface: 'bg-[#DB3725]/10'
}];
const MEASUREMENT_STEPS = [['Adoption', 'Active participation and completion by cohort'], ['Experience', 'Satisfaction, relevance, usability, and confidence to apply learning'], ['Impact', 'Manager-confirmed application and one business outcome with a baseline']];
export function ExecutiveValueReport() {
  const [isExpanded, setIsExpanded] = useState(false);
  const contentId = useId();
  const downloadExecutiveBrief = () => {
    const report = `Your Learners — ROI Report\n\nExecutive summary\nLearner feedback indicates that the professional development curriculum is delivering meaningful value: learners consistently describe it as practical, relevant, engaging, and easy to navigate. Financial ROI should be calculated after program cost, learner time, and the chosen business outcome are added.\n\nEvidence of value\n• 80% monthly learner participation, up 20% from the prior period.\n• Survey ratings were predominantly 4–5 out of 5.\n• Learners value immediately applicable leadership strategies and realistic workplace scenarios.\n• AI-focused learning is timely and valuable for healthcare professionals.\n\nRecommended next measures\n1. Track completion and active participation by cohort.\n2. Add manager confirmation of on-the-job application.\n3. Connect program cost, learner time, and one business outcome to calculate financial ROI.\n\nOpportunities to improve outcomes\nHelp more learners finish and apply their learning through protected learning time, manager encouragement, and targeted reminders for learners with modules remaining.`;
    const blob = new Blob([report], {
      type: 'text/plain;charset=utf-8'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'your-learners-roi-report.txt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  const openPhoebeForRoi = () => {
    openPhoebeChat({
      prompt: 'Help me tailor the ROI Report.',
      response: 'I can turn the current evidence into a leadership-ready narrative. Your strongest proof points are growing learner participation, consistently positive feedback, immediately applicable leadership strategies, and timely AI learning for healthcare teams.\n\nTo calculate a financial ROI, add three organization-specific inputs:\n• Total program cost\n• Learner and manager time invested\n• One measurable business outcome, such as reduced time to proficiency, retention, quality, or manager effectiveness\n\nWhich outcome is most important to your leadership team?',
      suggestedQuestions: ['Draft a leadership summary', 'Choose an ROI outcome', 'Define the cost inputs'],
      suggestionResponses: {
        'Draft a leadership summary': 'Here is a concise leadership message: “Learner feedback confirms the curriculum is relevant, practical, and easy to use. Participation has reached 80%, up 20% from the prior period, and employees are applying leadership and AI skills to real workplace situations. The next opportunity is helping more learners finish — through protected learning time, manager encouragement, and reminders for those with modules remaining.”',
        'Choose an ROI outcome': 'Choose the outcome that matters most to this audience: manager effectiveness, faster readiness for new responsibilities, employee retention, quality and safety, or time saved through improved AI fluency. Start with one outcome that already has a baseline measure.',
        'Define the cost inputs': 'Use the platform and content cost, learner learning hours multiplied by loaded hourly rate, manager or facilitator time, and any implementation support. Compare those inputs with the measurable benefit from the business outcome you selected.'
      }
    });
  };
  return <section aria-labelledby="executive-value-title" className="mb-8 overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm" data-id="element-1218" data-mp="1v8jfud6lb974">
      <div className="flex flex-col gap-4 p-5 sm:p-6 lg:flex-row lg:items-start" data-id="element-1219" data-mp="1q9f87qzbdjiw">
        <span className="flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-[#30718D] text-white shadow-sm" data-id="element-1220" data-mp="1lbdod3u2dg6d">
          <FileText size={24} aria-hidden="true" data-id="element-1221" data-mp="3kl3qa20ue27i" />
        </span>

        <div className="min-w-0 flex-1" data-id="element-1222" data-mp="3fpepxwh00ha5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between" data-id="element-1223" data-mp="3fmaece17ej9g">
            <div className="min-w-0" data-id="element-1224" data-mp="1dx4h7hz7v76k">
              <div className="flex items-center gap-2" data-id="element-1225" data-mp="1ipxmq82iejjp">
                <h2 id="executive-value-title" className="text-lg font-bold text-gray-900" data-id="element-1226" data-mp="1irqrfhtkatp2">
                  ROI Report
                </h2>
                <button type="button" onClick={openPhoebeForRoi} aria-label="Tailor the ROI Report with Phoebe" title="Tailor with Phoebe" className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[#30718D]/30 bg-white text-[#30718D] shadow-sm transition-colors hover:bg-[#30718D]/10 focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:ring-offset-2" data-id="element-1227" data-mp="18xrx34zqce7o">
                  <Sparkles size={15} aria-hidden="true" data-id="element-1228" data-mp="cbol79vcht1t" />
                </button>
              </div>
              <p className="mt-1 max-w-3xl text-sm leading-relaxed text-gray-600" data-id="element-1229" data-mp="g92sux3dnsrp">
                Connect learner adoption and end-of-course feedback to a clear
                executive case for continuing—and expanding—your learning investment.
              </p>
            </div>

            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center" data-id="element-1230" data-mp="ipc29gz1p9t4">
              <button type="button" onClick={() => setIsExpanded(expanded => !expanded)} aria-controls={contentId} aria-expanded={isExpanded} className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 transition-colors hover:border-[#30718D] hover:bg-[#30718D]/5 hover:text-[#30718D] focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:ring-offset-2 sm:w-auto" data-id="element-1231" data-mp="drixmsde4nuy">
                {isExpanded ? 'Hide details' : 'View details'}
                <ChevronDown size={16} aria-hidden="true" className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`} data-id="element-1232" data-mp="2cxn9ftxos0wc" />
              </button>
              <button type="button" onClick={downloadExecutiveBrief} className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#DB3725] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#C02F1F] focus:outline-none focus:ring-2 focus:ring-[#DB3725] focus:ring-offset-2 sm:w-auto" data-id="element-1233" data-mp="2mvbf3ewtysta">
                <Download size={14} aria-hidden="true" data-id="element-1234" data-mp="35mkprric7nmg" />
                Download Report
              </button>
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-2 border-t border-gray-100 pt-4 text-xs font-medium text-gray-500 sm:flex-row sm:flex-wrap sm:gap-x-5 sm:gap-y-2" data-id="element-1235" data-mp="3ak45kvuoo8gi">
            <span className="inline-flex items-center gap-1.5" data-id="element-1236" data-mp="3jlke0y1xu0i3">
              <span className="h-1.5 w-1.5 rounded-full bg-[#007558]" data-id="element-1237" data-mp="3jk0umykzj9c3" />
              80% active participation
            </span>
            <span className="inline-flex items-center gap-1.5" data-id="element-1238" data-mp="12zxbrsicu613">
              <span className="h-1.5 w-1.5 rounded-full bg-[#30718D]" data-id="element-1239" data-mp="131rwp6tmly56" />
              Predominantly 4–5/5 feedback
            </span>
            <span className="inline-flex items-center gap-1.5" data-id="element-1240" data-mp="tz8wyvl501n5">
              <span className="h-1.5 w-1.5 rounded-full bg-[#B77808]" data-id="element-1241" data-mp="u1240bznwnvo" />
              Financial ROI inputs required
            </span>
            <button type="button" onClick={openPhoebeForRoi} className="inline-flex items-center justify-center gap-2 self-start rounded-lg border border-[#30718D]/30 bg-white px-3 py-1.5 text-sm font-semibold text-[#30718D] transition-colors hover:bg-[#30718D]/5 focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:ring-offset-2 sm:ml-auto sm:self-auto" data-id="element-1242" data-mp="1mnwh2nu5gkz4">
              <MessageSquare size={16} aria-hidden="true" data-id="element-1243" data-mp="25m8xmune4x4q" />
              Tailor with Phoebe
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence initial={false} data-id="element-1244" data-mp="36jglog9yzrav">
        {isExpanded && <motion.div id={contentId} initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.22,
        ease: 'easeOut'
      }} className="overflow-hidden border-t border-gray-100" data-id="element-1245" data-mp="31k3n4y9qkvsg">
            <div className="p-5 sm:p-6" data-id="element-1246" data-mp="3pe52h676aktb">
              <div className="border-b border-gray-100 pb-5" data-id="element-1247" data-mp="3kgdakc6j2yp4">
                <div className="min-w-0 max-w-3xl" data-id="element-1248" data-mp="30x1m11g3rr5">
                  <p className="text-xs font-semibold uppercase tracking-wide text-[#30718D]" data-id="element-1249" data-mp="2zlqfg5o7hq0">
                    Executive value story
                  </p>
                  <h3 className="mt-1 text-xl font-bold leading-tight text-gray-900" data-id="element-1250" data-mp="c1bnb1ibn8v6">
                    The curriculum is earning learner trust and building relevant capability.
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-gray-600" data-id="element-1251" data-mp="qnsb3es6smry">
                    Your learners see the curriculum as practical and ready to
                    apply, particularly for leadership and AI-focused healthcare
                    development. The strongest next step is to help more learners
                    finish and apply it — through protected learning time, manager
                    encouragement, and reminders for those with modules remaining.
                  </p>
                </div>
              </div>

              <section aria-labelledby="evidence-title" className="mt-6" data-id="element-1252" data-mp="cptxwcplqwe1">
                <h4 id="evidence-title" className="text-sm font-bold text-gray-900" data-id="element-1253" data-mp="3u6219vcjpv0p">
                  Evidence of value
                </h4>
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-[repeat(auto-fit,minmax(13.5rem,1fr))]" data-id="element-1254" data-mp="2wd3p7ljzf9u">
                  {EVIDENCE.map(item => <article key={item.label} className="min-w-0 rounded-lg border border-gray-100 bg-gray-50 p-4" data-id="element-1255" data-mp="3q242jlr4um86">
                      <div className="flex items-start gap-3" data-id="element-1256" data-mp="1qw8fhx0xusxi">
                        <span className={`flex h-9 w-9 flex-none items-center justify-center rounded-lg ${item.surface} ${item.accent}`} data-id="element-1257" data-mp="1lxdoi7sthy2b">
                          {item.icon}
                        </span>
                        <div className="min-w-0" data-id="element-1258" data-mp="2ekrn0ac5z1oj">
                          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500" data-id="element-1259" data-mp="29n7zskxhnekg">
                            {item.label}
                          </p>
                          <p className="mt-1 break-words text-sm font-bold leading-snug text-gray-900" data-id="element-1260" data-mp="25nhcir767gko">
                            {item.value}
                          </p>
                        </div>
                      </div>
                      <p className="mt-3 break-words text-sm leading-relaxed text-gray-600" data-id="element-1261" data-mp="p7efydco2zjq">
                        {item.detail}
                      </p>
                    </article>)}
                </div>
              </section>

              <section aria-labelledby="measurement-title" className="mt-6 rounded-lg border border-[#30718D]/20 bg-[#EAF4F7] p-4 sm:p-5" data-id="element-1262" data-mp="35u31ubrc0ou2">
                <div className="flex flex-col gap-1" data-id="element-1263" data-mp="3emofjdg3w5c1">
                  <h4 id="measurement-title" className="text-sm font-bold text-gray-900" data-id="element-1264" data-mp="3jl829g2ixl5g">
                    Complete the financial ROI case
                  </h4>
                  <p className="max-w-4xl text-sm leading-relaxed text-gray-600" data-id="element-1265" data-mp="3oi80x5x1nlmj">
                    Financial return is not estimated here because cost and business
                    outcome assumptions have not yet been provided. Use the same
                    reporting cycle to connect this evidence to one measurable outcome.
                  </p>
                </div>
                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-[repeat(auto-fit,minmax(13.5rem,1fr))]" data-id="element-1266" data-mp="237hotipilkj5">
                  {MEASUREMENT_STEPS.map(([label, detail], index) => <div key={label} className="min-w-0 border-l-2 border-[#7AB6CC] pl-3" data-id="element-1267" data-mp="13twmsbwqkny0" data-mpi={index}>
                      <p className="text-xs font-semibold uppercase tracking-wide text-[#30718D]" data-id="element-1268" data-mp="1hto4onqdioha">
                        {index + 1}. {label}
                      </p>
                      <p className="mt-1 break-words text-sm leading-relaxed text-gray-700" data-id="element-1269" data-mp="p5sabpoa18no">
                        {detail}
                      </p>
                    </div>)}
                </div>
              </section>
            </div>
          </motion.div>}
      </AnimatePresence>
    </section>;
}